import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { Listbox, ListboxItem, PopoverContent, Selection, Tab, Tabs } from "@nextui-org/react";
import { memo } from "react";

import Trans from "@/components/Trans/Trans";
import { IPlanData } from "@/types";
import { getCurrencySymbol, getPurchaseData } from "@/utils";

import { Row, Text } from "../customComponents";

const TABS_CLASS_NAMES = {
  base: "min-w-[175px] w-full grow",
  tabList: "dark:bg-gray-500 rounded-md w-full h-9",
  tabContent: "font-medium text-[16px]",
  cursor: "rounded-md",
  panel: "pb-0 w-full",
};

export type PlanPopoverContentProps = {
  readonly effectiveFrequency: BillingCycle;
  readonly hasQuarterlyPlans: boolean;
  readonly onFrequencyChange: (key: unknown) => void;
  readonly selectedPlanId: string | null;
  readonly selectedPlans: IPlanData[];
  readonly onPlanChange: (plan: Selection) => void;
};

function PlanPopoverContent({
  effectiveFrequency,
  hasQuarterlyPlans,
  onFrequencyChange,
  selectedPlanId,
  selectedPlans,
  onPlanChange,
}: PlanPopoverContentProps) {
  const selectedKeys = new Set(selectedPlanId ? [selectedPlanId] : []);
  return (
    <PopoverContent className="bg-gray-100 p-3 rounded-xl shadow-lg">
      <Tabs
        selectedKey={effectiveFrequency}
        onSelectionChange={onFrequencyChange}
        color="primary"
        aria-label="Monthly/Anually Switch"
        classNames={TABS_CLASS_NAMES}
      >
        <Tab key={BillingCycle.ANNUAL} title={<Trans i18nKey="subscription:checkout.annually" />} className="bg-transparent px-0 h-full text-white" />
        {hasQuarterlyPlans ? (
          <Tab
            data-testid="QuarterlyPlanTab"
            key={BillingCycle.QUARTERLY}
            title={<Trans i18nKey="subscription:checkout.quarterly" />}
            className="bg-transparent px-0 h-full text-white"
          />
        ) : null}
        <Tab key={BillingCycle.MONTHLY} title={<Trans i18nKey="subscription:checkout.monthly" />} className="bg-transparent px-0 h-full text-white" />
      </Tabs>
      <Listbox
        aria-label="Select a plan"
        variant="flat"
        disallowEmptySelection
        selectionMode="single"
        selectedKeys={selectedKeys}
        onSelectionChange={onPlanChange}
      >
        {selectedPlans.map((purchaseOption: IPlanData, idx: number) => {
          const { tier, price } = getPurchaseData(purchaseOption);
          const currency = getCurrencySymbol(purchaseOption?.currency);
          return (
            <ListboxItem
              key={purchaseOption?.plan_id}
              showDivider={idx + 1 < selectedPlans.length}
              className={"data-[selected=true]:bg-gray-500"}
              hideSelectedIcon
            >
              <Row className="justify-between">
                <Text>{tier}</Text>
                <Text>
                  {currency}
                  {price}
                </Text>
              </Row>
            </ListboxItem>
          );
        })}
      </Listbox>
    </PopoverContent>
  );
}

export default memo(PlanPopoverContent);
